import { useEffect, useState } from "react";
import { Banknote, CircleSlash, Receipt, RotateCcw, Wallet } from "lucide-react";
import { DatePicker, Select } from "antd";
import dayjs from "dayjs";

import { Reactable } from "../../components/ui/reactable";
import { Button } from "../../components/ui/button";
import { DialogModal } from "../../components/ui/dialogModal";
import { Input, Textarea } from "../../components/ui/input";
import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { checkUserPermissionFunc } from "../../components/common/CommonFunctions";
import { EMPLOYEE_DEPOSITS, EMPLOYEE_DEPOSITS_SUMMARY, EMPLOYEE_PERSONAL_INFO } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";
import { modalDescriptionFieldProps, modalFieldLabelClass, modalInputClass, modalTextareaClass } from "../../components/ui/fieldStyles";
import { DepositMovementTrace, canReverseDepositTxn } from "../../components/employee/DepositMovementTrace";
import { formatDepositTraceLine } from "../../utils/depositStatementDisplay";

const PURPOSE = [
    { value: "security", label: "Security" },
    { value: "equipment", label: "Equipment" },
    { value: "uniform", label: "Uniform" },
    { value: "accommodation", label: "Accommodation" },
    { value: "other", label: "Other" },
];

const COLLECTION_MODE = [
    { value: "cash", label: "Cash" },
    { value: "bank_transfer", label: "Bank transfer" },
    { value: "cheque", label: "Cheque" },
    { value: "salary_deduction", label: "Deduct from salary" },
];

// Direction is spelled out in the label, not just implied by colour — this is
// the dialog where picking the wrong one moves real money the wrong way.
const MOVEMENT = [
    { value: "collected", label: "Collected  (+ money received)" },
    { value: "applied", label: "Applied  (− taken against a fine or dues)" },
    { value: "refunded", label: "Refunded  (− paid back)" },
    { value: "forfeited", label: "Forfeited  (− kept by the organisation)" },
];

const NEEDS_REASON = ["applied", "forfeited"];

const STATUS_CHIP = {
    active: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
    closed: "bg-gray-100 text-gray-600 dark:bg-gray-700/40 dark:text-gray-300",
    forfeited: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
};

const money = (value) =>
    Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const EMPTY_OPEN = {
    employee_id: null,
    purpose: "security",
    agreed_amount: "",
    collection_mode: "cash",
    collected_amount: "",
    instalment_count: "",
    reference: "",
    opened_on: dayjs(),
    notes: "",
};

const EMPTY_MOVE = { txn_type: "collected", amount: "", reference: "", reason: "", txn_date: dayjs() };

const EmployeeDeposits = () => {
    const { fetch, create, update } = useApiMethods();

    const [rows, setRows] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [serverTrigger, setServerTrigger] = useState(false);
    const [summary, setSummary] = useState(null);
    const [employees, setEmployees] = useState([]);

    const [openDialog, setOpenDialog] = useState(false);
    const [openForm, setOpenForm] = useState(EMPTY_OPEN);
    const [busy, setBusy] = useState(false);

    const [moveFor, setMoveFor] = useState(null);
    const [moveForm, setMoveForm] = useState(EMPTY_MOVE);

    const [statementFor, setStatementFor] = useState(null);
    const [statement, setStatement] = useState(null);
    const [reverseTxn, setReverseTxn] = useState(null);
    const [reverseReason, setReverseReason] = useState("");

    const [closeFor, setCloseFor] = useState(null);
    const [closeAs, setCloseAs] = useState("refunded");
    const [closeReason, setCloseReason] = useState("");

    // Admins bypass the permission tree, matching the API (which has always
    // allowed admins through) and the eight other screens in this app that do
    // the same. Without it, a permission column added after the admin group was
    // created leaves the page rendering but unusable: no Open button, empty table.
    const isAdmin =
        JSON.parse(sessionStorage.getItem("is_admin") || "false") ||
        JSON.parse(sessionStorage.getItem("is_super_admin") || "false") ||
        JSON.parse(sessionStorage.getItem("is_system_admin") || "false");
    const { add: addGranted, edit: editGranted } =
        checkUserPermissionFunc("employee", "Employee Deposits", "SubMenu");
    const addPermission = addGranted || isAdmin;
    const editPermission = editGranted || isAdmin;

    // The org-wide liability. Its own endpoint because it reads the ledger once
    // instead of decrypting every deposit's balance twice.
    const loadSummary = async () => {
        const response = await fetch(EMPLOYEE_DEPOSITS_SUMMARY, {});
        if (response?.status === "success") setSummary(response.data);
    };

    useEffect(() => {
        loadSummary();
        // Employee picker for opening a deposit. drop_down keeps it to the
        // lightweight list rather than a paginated page of full records.
        (async () => {
            const response = await fetch(EMPLOYEE_PERSONAL_INFO, { dropdown: true });
            const list = response?.data?.result || response?.data || [];
            if (Array.isArray(list)) setEmployees(list);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serverTrigger]);

    const employeeOptions = employees.map((e) => ({
        value: e.id,
        label: `${e.first_name || e.employee_name || ""} ${e.last_name || ""} (${e.employee_id || e.id})`.trim(),
    }));

    const refresh = () => {
        setServerTrigger((prev) => !prev);
        loadSummary();
    };

    const openDeposit = async () => {
        if (!openForm.employee_id) {
            ToastMsg("error", "Please choose an employee");
            return;
        }
        const isSalary = openForm.collection_mode === "salary_deduction";
        const agreed = openForm.agreed_amount === "" ? 0 : Number(openForm.agreed_amount);
        const collected =
            isSalary || openForm.collected_amount === ""
                ? 0
                : Number(openForm.collected_amount);
        if (!isSalary && agreed > 0 && collected > agreed) {
            ToastMsg("error", "Collected amount cannot exceed the agreed deposit limit");
            return;
        }
        setBusy(true);
        const response = await create(EMPLOYEE_DEPOSITS, {
            employee_id: openForm.employee_id,
            purpose: openForm.purpose,
            agreed_amount: agreed,
            collection_mode: openForm.collection_mode,
            collected_amount: collected,
            instalment_count:
                openForm.collection_mode === "salary_deduction" && openForm.instalment_count !== ""
                    ? Number(openForm.instalment_count)
                    : null,
            reference: openForm.reference,
            opened_on: openForm.opened_on ? dayjs(openForm.opened_on).format("YYYY-MM-DD") : null,
            notes: openForm.notes,
        });
        setBusy(false);
        if (response?.status === "success") {
            ToastMsg("success", response.message);
            setOpenDialog(false);
            setOpenForm(EMPTY_OPEN);
            refresh();
        } else {
            const fieldError = response?.errors && Object.values(response.errors)[0];
            ToastMsg("error", fieldError || response?.message || "Could not open the deposit");
        }
    };

    const recordMovement = async () => {
        const amount = moveForm.amount === "" ? 0 : Number(moveForm.amount);
        if (moveForm.txn_type === "collected" && moveFor && amount > 0) {
            const agreed = Number(moveFor.agreed_amount || 0);
            const already = Number(moveFor.collected || 0);
            if (agreed > 0 && already + amount > agreed) {
                ToastMsg(
                    "error",
                    `Cannot collect more than the agreed deposit limit (₹${agreed.toLocaleString("en-IN")})`,
                );
                return;
            }
        }
        setBusy(true);
        const response = await update(EMPLOYEE_DEPOSITS, {
            id: moveFor.id,
            txn_type: moveForm.txn_type,
            amount,
            reference: moveForm.reference,
            reason: moveForm.reason,
            txn_date: moveForm.txn_date ? dayjs(moveForm.txn_date).format("YYYY-MM-DD") : null,
        });
        setBusy(false);
        if (response?.status === "success") {
            ToastMsg("success", response.message);
            setMoveFor(null);
            setMoveForm(EMPTY_MOVE);
            refresh();
        } else {
            ToastMsg("error", response?.message || "Could not record the movement");
        }
    };

    const showStatement = async (row) => {
        setStatementFor(row);
        setStatement(null);
        const response = await fetch(EMPLOYEE_DEPOSITS, { id: row.id });
        if (response?.status === "success") setStatement(response.data);
        else ToastMsg("error", response?.message || "Could not load the statement");
    };

    const doReverse = async () => {
        setBusy(true);
        const response = await update(EMPLOYEE_DEPOSITS, {
            id: statementFor.id,
            reverse_txn_id: reverseTxn.id,
            reason: reverseReason,
        });
        setBusy(false);
        if (response?.status === "success") {
            ToastMsg("success", response.message);
            setReverseTxn(null);
            setReverseReason("");
            showStatement(statementFor);
            refresh();
        } else {
            ToastMsg("error", response?.message || "Could not reverse the movement");
        }
    };

    const doClose = async () => {
        setBusy(true);
        const response = await update(EMPLOYEE_DEPOSITS, {
            id: closeFor.id,
            close: true,
            settle_as: closeFor.balance > 0 ? closeAs : null,
            reason: closeReason,
        });
        setBusy(false);
        if (response?.status === "success") {
            ToastMsg("success", response.message);
            setCloseFor(null);
            setCloseReason("");
            refresh();
        } else {
            ToastMsg("error", response?.message || "Could not close the deposit");
        }
    };

    const columns = [
        {
            accessor: "employee_name",
            Header: "Employee",
            sortable: true,
            filterable: true,
            Cell: ({ row }) => (
                <>
                    <span className="font-medium text-gray-900 dark:text-white">
                        {row.employee_name || "-"}
                    </span>
                    <span className="block text-xs text-muted-foreground">
                        {row.employee_code}
                        {row.department ? ` · ${row.department}` : ""}
                    </span>
                </>
            ),
        },
        {
            accessor: "purpose",
            Header: "For",
            filterable: true,
            Cell: ({ value }) => <span className="capitalize">{value || "-"}</span>,
        },
        {
            accessor: "agreed_amount",
            Header: "Agreed",
            sortable: true,
            Cell: ({ value }) => <span className="tabular-nums">{money(value)}</span>,
        },
        {
            accessor: "collected",
            Header: "Collected",
            Cell: ({ value }) => <span className="tabular-nums">{money(value)}</span>,
        },
        {
            accessor: "applied",
            Header: "Taken",
            Cell: ({ row }) => (
                <span className="tabular-nums">
                    {money(Number(row.applied || 0) + Number(row.forfeited || 0))}
                </span>
            ),
        },
        {
            accessor: "refunded",
            Header: "Refunded",
            Cell: ({ value }) => <span className="tabular-nums">{money(value)}</span>,
        },
        {
            // The liability. Emphasised because it is the number that matters and
            // the one finance asks about.
            accessor: "balance",
            Header: "Held",
            sortable: true,
            Cell: ({ value }) => (
                <span
                    className={cn(
                        "tabular-nums font-semibold",
                        Number(value) > 0 ? "text-sidebar-primary" : "text-muted-foreground",
                    )}
                >
                    {money(value)}
                </span>
            ),
        },
        {
            accessor: "status",
            Header: "Status",
            filterable: true,
            Cell: ({ value }) => (
                <span
                    className={cn(
                        "px-2 py-1 text-xs font-medium rounded-full capitalize",
                        STATUS_CHIP[value] || STATUS_CHIP.active,
                    )}
                >
                    {value || "active"}
                </span>
            ),
        },
        {
            accessor: "action",
            Header: "Actions",
            sortable: false,
            Cell: ({ row }) => (
                <div className="flex items-center gap-1">
                    <Button
                        variant="ghost"
                        size="sm"
                        title="Statement"
                        className="text-sidebar-primary hover:bg-blue-50 dark:hover:bg-gray-700"
                        onClick={() => showStatement(row)}
                    >
                        <Receipt className="h-4 w-4" />
                    </Button>
                    {editPermission && row.status === "active" && (
                        <>
                            <Button
                                variant="ghost"
                                size="sm"
                                title="Record a movement"
                                className="text-emerald-600 hover:bg-emerald-50 dark:hover:bg-gray-700"
                                onClick={() => {
                                    setMoveFor(row);
                                    setMoveForm(EMPTY_MOVE);
                                }}
                            >
                                <Banknote className="h-4 w-4" />
                            </Button>
                            <Button
                                variant="ghost"
                                size="sm"
                                title="Close the deposit"
                                className="text-rose-600 hover:bg-rose-50 dark:hover:bg-gray-700"
                                onClick={() => {
                                    setCloseFor(row);
                                    setCloseAs("refunded");
                                    setCloseReason("");
                                }}
                            >
                                <CircleSlash className="h-4 w-4" />
                            </Button>
                        </>
                    )}
                </div>
            ),
        },
    ];

    const isSalaryMode = openForm.collection_mode === "salary_deduction";
    const moveNeedsReason = NEEDS_REASON.includes(moveForm.txn_type);
    const remainingCollectible = moveFor
        ? Math.max(0, Number(moveFor.agreed_amount || 0) - Number(moveFor.collected || 0))
        : 0;
    const collectedAfter =
        moveFor && moveForm.amount !== "" && moveForm.txn_type === "collected"
            ? Number(moveFor.collected || 0) + Number(moveForm.amount)
            : null;
    const balanceAfter =
        moveFor && moveForm.amount !== ""
            ? moveForm.txn_type === "collected"
                ? Number(moveFor.balance) + Number(moveForm.amount)
                : Number(moveFor.balance) - Number(moveForm.amount)
            : moveFor?.balance;

    return (
        <ContainerCard>
            <ModuleTitleCard
                Icon={Wallet}
                title="Employee Deposits"
                subtitle="Money held on behalf of employees, and what has happened to it"
                totalRecords={totalRecords}
                buttonEnable={!!addPermission}
                setOpenDialog={setOpenDialog}
                buttonName="Open Deposit"
            />

            {/* The liability, stated up front — it is a figure finance owns. */}
            {summary && (
                <div className="mx-3 mb-2 flex flex-wrap items-baseline gap-x-6 gap-y-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 px-3 py-2">
                    <span className="text-sm">
                        Currently held{" "}
                        <b className="tabular-nums text-sidebar-primary">₹ {money(summary.held)}</b>
                    </span>
                    <span className="text-xs text-muted-foreground tabular-nums">
                        across {summary.deposits_with_balance} deposit
                        {summary.deposits_with_balance === 1 ? "" : "s"} · {summary.deposits_total} on record
                    </span>
                </div>
            )}

            <Reactable
                pagename="employee-deposits"
                columns={columns}
                data={rows}
                setData={setRows}
                setTotalCount={setTotalRecords}
                url={EMPLOYEE_DEPOSITS}
                serverSide={true}
                serverTrigger={serverTrigger}
                rowSize="sm"
                isCSVRequired={true}
                isPDFRequired={true}
                isDateRequired={false}
            />

            {/* Open a deposit */}
            <DialogModal
                open={openDialog}
                onClose={() => {
                    setOpenDialog(false);
                    setOpenForm(EMPTY_OPEN);
                }}
                onSave={openDeposit}
                title="Open a deposit"
                saveLabel="Open"
                closeLabel="Cancel"
                loading={busy}
                width="620px"
            >
                <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
                    <div className="space-y-2">
                        <label className={modalFieldLabelClass}>
                            Employee <span className="text-red-500">*</span>
                        </label>
                        <Select
                            className="w-full"
                            value={openForm.employee_id}
                            options={employeeOptions}
                            placeholder="Search for an employee"
                            showSearch
                            optionFilterProp="label"
                            onChange={(value) => setOpenForm((p) => ({ ...p, employee_id: value }))}
                            disabled={busy}
                        />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="space-y-2">
                            <label className={modalFieldLabelClass}>For</label>
                            <Select
                                className="w-full"
                                value={openForm.purpose}
                                options={PURPOSE}
                                onChange={(value) => setOpenForm((p) => ({ ...p, purpose: value }))}
                                disabled={busy}
                            />
                        </div>
                        <div className="space-y-2">
                            <label htmlFor="agreed" className={modalFieldLabelClass}>
                                Agreed amount <span className="text-red-500">*</span>
                            </label>
                            <Input
                                id="agreed"
                                type="number"
                                min="0"
                                value={openForm.agreed_amount}
                                onChange={(e) => setOpenForm((p) => ({ ...p, agreed_amount: e.target.value }))}
                                placeholder="5000"
                                disabled={busy}
                            />
                        </div>
                        <div className="space-y-2">
                            <label className={modalFieldLabelClass}>Opened on</label>
                            <DatePicker
                                className="w-full"
                                format="DD-MM-YYYY"
                                value={openForm.opened_on}
                                onChange={(date) => setOpenForm((p) => ({ ...p, opened_on: date }))}
                                disabled={busy}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="space-y-2">
                            <label className={modalFieldLabelClass}>
                                How it is collected
                            </label>
                            <Select
                                className="w-full"
                                value={openForm.collection_mode}
                                options={COLLECTION_MODE}
                                onChange={(value) => setOpenForm((p) => ({ ...p, collection_mode: value }))}
                                disabled={busy}
                            />
                        </div>
                        {isSalaryMode ? (
                            <div className="space-y-2">
                                <label htmlFor="inst" className={modalFieldLabelClass}>
                                    Over how many months <span className="text-red-500">*</span>
                                </label>
                                <Input
                                    id="inst"
                                    type="number"
                                    min="1"
                                    value={openForm.instalment_count}
                                    onChange={(e) => setOpenForm((p) => ({ ...p, instalment_count: e.target.value }))}
                                    placeholder="5"
                                    disabled={busy}
                                />
                            </div>
                        ) : (
                            <div className="space-y-2">
                                <label htmlFor="collected" className={modalFieldLabelClass}>
                                    Received now
                                </label>
                                <Input
                                    id="collected"
                                    type="number"
                                    min="0"
                                    value={openForm.collected_amount}
                                    onChange={(e) => setOpenForm((p) => ({ ...p, collected_amount: e.target.value }))}
                                    placeholder="5000"
                                    disabled={busy}
                                />
                            </div>
                        )}
                        <div className="space-y-2">
                            <label htmlFor="ref" className={modalFieldLabelClass}>
                                Reference
                            </label>
                            <Input
                                id="ref"
                                value={openForm.reference}
                                onChange={(e) => setOpenForm((p) => ({ ...p, reference: e.target.value }))}
                                placeholder="Receipt or cheque number"
                                disabled={busy}
                            />
                        </div>
                    </div>

                    <p className="text-[11px] text-muted-foreground">
                        {isSalaryMode
                            ? "Nothing is collected yet — the instalments are deducted month by month and the balance builds up as they are taken."
                            : "Anything entered as received now is recorded on the ledger straight away."}
                    </p>

                    <div className="space-y-2">
                        <label htmlFor="notes" className={modalFieldLabelClass}>
                            Notes
                        </label>
                        <Textarea
                            id="notes"
                            rows={2}
                            value={openForm.notes}
                            onChange={(e) => setOpenForm((p) => ({ ...p, notes: e.target.value }))}
                            disabled={busy}
                        />
                    </div>
                </form>
            </DialogModal>

            {/* Record a movement */}
            <DialogModal
                open={!!moveFor}
                onClose={() => {
                    setMoveFor(null);
                    setMoveForm(EMPTY_MOVE);
                }}
                onSave={recordMovement}
                title={`Deposit movement — ${moveFor?.employee_name || ""}`}
                saveLabel="Record"
                closeLabel="Cancel"
                loading={busy}
                width="520px"
            >
                <div className="space-y-4">
                    {/* The remaining balance, first thing. It is the number that
                        decides whether the amount below is even allowed, so it
                        belongs above the field and not under it. */}
                    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 px-3 py-2.5">
                        <div className="flex items-baseline justify-between gap-3">
                            <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                                Remaining deposit
                            </span>
                            <b className="text-lg tabular-nums text-gray-900 dark:text-gray-100">
                                ₹ {money(moveFor?.balance)}
                            </b>
                        </div>
                        {moveForm.txn_type === "collected" && Number(moveFor?.agreed_amount || 0) > 0 && (
                            <div className="mt-1.5 flex items-baseline justify-between gap-3 border-t border-gray-200 dark:border-gray-700 pt-1.5">
                                <span className="text-xs text-gray-600 dark:text-gray-400">Still collectible (agreed limit)</span>
                                <b className="tabular-nums text-gray-900 dark:text-gray-100">
                                    ₹ {money(remainingCollectible)}
                                </b>
                            </div>
                        )}
                        {moveForm.amount !== "" && (
                            <div className="mt-1.5 flex items-baseline justify-between gap-3 border-t border-gray-200 dark:border-gray-700 pt-1.5">
                                <span className="text-xs text-gray-600 dark:text-gray-400">After this movement</span>
                                <b
                                    className={cn(
                                        "tabular-nums",
                                        balanceAfter < 0
                                            ? "text-rose-600 dark:text-rose-400"
                                            : "text-gray-900 dark:text-gray-100",
                                    )}
                                >
                                    ₹ {money(balanceAfter)}
                                </b>
                            </div>
                        )}
                        {moveForm.amount !== "" && balanceAfter < 0 && (
                            <p className="mt-1 text-[11px] text-rose-600 dark:text-rose-400">
                                More than is held — this will be refused.
                            </p>
                        )}
                        {collectedAfter !== null
                            && Number(moveFor?.agreed_amount || 0) > 0
                            && collectedAfter > Number(moveFor.agreed_amount) && (
                            <p className="mt-1 text-[11px] text-rose-600 dark:text-rose-400">
                                Exceeds agreed deposit limit — this will be refused.
                            </p>
                        )}
                    </div>
                    <div className="space-y-2">
                        <label className={modalFieldLabelClass}>
                            What happened <span className="text-red-500">*</span>
                        </label>
                        <Select
                            className="w-full"
                            value={moveForm.txn_type}
                            options={MOVEMENT}
                            onChange={(value) => setMoveForm((p) => ({ ...p, txn_type: value }))}
                            disabled={busy}
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <label htmlFor="amt" className={modalFieldLabelClass}>
                                Amount <span className="text-red-500">*</span>
                            </label>
                            <Input
                                id="amt"
                                type="number"
                                min="0"
                                value={moveForm.amount}
                                onChange={(e) => setMoveForm((p) => ({ ...p, amount: e.target.value }))}
                                disabled={busy}
                            />
                        </div>
                        <div className="space-y-2">
                            <label className={modalFieldLabelClass}>Date</label>
                            <DatePicker
                                className="w-full"
                                format="DD-MM-YYYY"
                                value={moveForm.txn_date}
                                onChange={(date) => setMoveForm((p) => ({ ...p, txn_date: date }))}
                                disabled={busy}
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label htmlFor="mref" className={modalFieldLabelClass}>
                            Reference
                        </label>
                        <Input
                            id="mref"
                            value={moveForm.reference}
                            onChange={(e) => setMoveForm((p) => ({ ...p, reference: e.target.value }))}
                            placeholder="Receipt, cheque or transaction number"
                            disabled={busy}
                        />
                    </div>

                    <div className="space-y-2">
                        <label htmlFor="mreason" className={modalFieldLabelClass}>
                            Reason {moveNeedsReason && <span className="text-red-500">*</span>}
                        </label>
                        <Input
                            id="mreason"
                            value={moveForm.reason}
                            onChange={(e) => setMoveForm((p) => ({ ...p, reason: e.target.value }))}
                            placeholder="Lost ID card"
                            maxLength={255}
                            disabled={busy}
                        />
                        {moveNeedsReason && (
                            <p className="text-[11px] text-muted-foreground">
                                Required, and kept on the record — this is what answers the question if the
                                employee asks later why the money came back short.
                            </p>
                        )}
                    </div>
                </div>
            </DialogModal>

            {/* Statement */}
            <DialogModal
                open={!!statementFor}
                onClose={() => {
                    setStatementFor(null);
                    setStatement(null);
                }}
                title={`Statement — ${statementFor?.employee_name || ""}`}
                closeLabel="Close"
                hideSaveButton={true}
                width="820px"
            >
                {!statement ? (
                    <p className="py-8 text-center text-muted-foreground">Loading</p>
                ) : (
                    <div className="space-y-3">
                        <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
                            <span className="capitalize text-muted-foreground">{statement.purpose} deposit</span>
                            <span>Agreed <b className="tabular-nums">₹ {money(statement.agreed_amount)}</b></span>
                            <span>Held <b className="tabular-nums text-sidebar-primary">₹ {money(statement.balance)}</b></span>
                        </div>

                        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                            <table className="min-w-full text-sm">
                                <thead className="bg-gray-50 dark:bg-gray-800">
                                    <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                                        <th className="px-3 py-2 font-medium">Date</th>
                                        <th className="px-3 py-2 font-medium">Movement</th>
                                        <th className="px-3 py-2 font-medium text-right">Amount</th>
                                        <th className="px-3 py-2 font-medium text-right">Balance</th>
                                        <th className="px-3 py-2 font-medium">Reference / reason</th>
                                        <th className="px-3 py-2 font-medium"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(statement.statement || []).map((txn) => (
                                        <tr key={txn.id} className="border-t border-gray-200 dark:border-gray-700">
                                            <td className="px-3 py-2 whitespace-nowrap tabular-nums">
                                                {txn.txn_date ? dayjs(txn.txn_date).format("DD-MM-YYYY") : "-"}
                                            </td>
                                            <td className="px-3 py-2">
                                                <DepositMovementTrace txn={txn} />
                                            </td>
                                            <td
                                                className={cn(
                                                    "px-3 py-2 text-right tabular-nums",
                                                    txn.direction === "in"
                                                        ? "text-emerald-700 dark:text-emerald-400"
                                                        : "text-rose-700 dark:text-rose-400",
                                                )}
                                            >
                                                {txn.direction === "in" ? "+" : "−"} {money(txn.amount)}
                                            </td>
                                            <td className="px-3 py-2 text-right tabular-nums font-medium">
                                                {money(txn.balance)}
                                            </td>
                                            <td className="px-3 py-2 text-xs text-muted-foreground">
                                                {txn.reference}
                                                {txn.reference && txn.reason ? " · " : ""}
                                                {txn.reason}
                                            </td>
                                            <td className="px-3 py-2 text-right">
                                                {/* No edit, ever. A mistake is corrected by a
                                                    reversal that stands beside the original. */}
                                                {editPermission &&
                                                    canReverseDepositTxn(txn, statementFor?.status) && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            title="Reverse this movement"
                                                            className="text-muted-foreground"
                                                            onClick={() => {
                                                                setReverseTxn(txn);
                                                                setReverseReason("");
                                                            }}
                                                        >
                                                            <RotateCcw className="h-4 w-4" />
                                                        </Button>
                                                    )}
                                            </td>
                                        </tr>
                                    ))}
                                    {!statement.statement?.length && (
                                        <tr>
                                            <td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">
                                                Nothing collected yet.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </DialogModal>

            {/* Reverse */}
            <DialogModal
                open={!!reverseTxn}
                onClose={() => setReverseTxn(null)}
                onSave={doReverse}
                title="Reverse this movement"
                saveLabel="Reverse"
                closeLabel="Cancel"
                loading={busy}
                width="460px"
            >
                <div className="space-y-3">
                    {reverseTxn && (
                        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 px-3 py-2.5 text-sm">
                            <p className="text-xs font-medium text-muted-foreground mb-1">Movement being reversed</p>
                            <p className="tabular-nums text-gray-900 dark:text-gray-100">
                                {formatDepositTraceLine(reverseTxn)}
                            </p>
                        </div>
                    )}
                    <p className="text-sm text-muted-foreground">
                        The original stays on the statement with this correction beside it. Nothing is edited
                        or removed.
                    </p>
                    <div className="space-y-2">
                        <label htmlFor="rvreason" className={modalFieldLabelClass}>
                            Reason <span className="text-red-500">*</span>
                        </label>
                        <Input
                            id="rvreason"
                            value={reverseReason}
                            onChange={(e) => setReverseReason(e.target.value)}
                            placeholder="Recorded twice"
                            maxLength={255}
                            disabled={busy}
                        />
                    </div>
                </div>
            </DialogModal>

            {/* Close */}
            <DialogModal
                open={!!closeFor}
                onClose={() => setCloseFor(null)}
                onSave={doClose}
                title={`Close the deposit — ${closeFor?.employee_name || ""}`}
                saveLabel="Close deposit"
                closeLabel="Cancel"
                loading={busy}
                width="480px"
            >
                <div className="space-y-3">
                    {closeFor?.balance > 0 ? (
                        <>
                            <p className="text-sm">
                                <b className="tabular-nums">₹ {money(closeFor.balance)}</b> is still held. Decide
                                what happens to it — a deposit cannot be closed with money left on it.
                            </p>
                            <div className="space-y-2">
                                <label className={modalFieldLabelClass}>
                                    Settle it as
                                </label>
                                <Select
                                    className="w-full"
                                    value={closeAs}
                                    options={[
                                        { value: "refunded", label: "Refunded to the employee" },
                                        { value: "forfeited", label: "Forfeited — kept by the organisation" },
                                    ]}
                                    onChange={setCloseAs}
                                    disabled={busy}
                                />
                            </div>
                            <div className="space-y-2">
                                <label htmlFor="clreason" className={modalFieldLabelClass}>
                                    Reason {closeAs === "forfeited" && <span className="text-red-500">*</span>}
                                </label>
                                <Input
                                    id="clreason"
                                    value={closeReason}
                                    onChange={(e) => setCloseReason(e.target.value)}
                                    placeholder={closeAs === "forfeited" ? "Equipment not returned" : "Left the organisation"}
                                    maxLength={255}
                                    disabled={busy}
                                />
                            </div>
                        </>
                    ) : (
                        <p className="text-sm text-muted-foreground">
                            Nothing is held on this deposit, so it can be closed as settled.
                        </p>
                    )}
                </div>
            </DialogModal>
        </ContainerCard>
    );
};

export default EmployeeDeposits;
